import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useReassignDomain, type CDNDomain } from '@/api/manticore';
import { type AvailableService } from '@/api/services';
import { ServiceSelect } from '@/components/service-select/ServiceSelect';

/** Pull a human message out of an axios-style error, with a sensible fallback. */
function errorMessage(e: unknown, fallback: string): string {
  const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return msg || fallback;
}

/**
 * Move a single domain's CDN binding to another service. Only same-provider,
 * same-organization targets are offered (the backend enforces the same rule and
 * the binding id stays stable, so analytics history follows the domain).
 */
export function DomainMoveDialog({
  open,
  onOpenChange,
  domain,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  domain: CDNDomain | null;
  onDone: () => void;
}) {
  const { show } = useToast();
  const navigate = useNavigate();
  const [target, setTarget] = useState<AvailableService | null>(null);
  const reassign = useReassignDomain();

  // Providers this domain already has a binding for — the move keeps the same
  // provider, so only those services are valid targets.
  const providerNames = useMemo(
    () => new Set((domain?.bindings ?? []).map((b) => b.provider?.toLowerCase()).filter(Boolean)),
    [domain],
  );

  // Same-provider CDN services only (the backend rejects others anyway, but
  // hiding them keeps the picker honest).
  const sameProviderCdn = useCallback(
    (s: AvailableService) =>
      s.provider?.type === 'cdn' &&
      (providerNames.size === 0 || providerNames.has(s.provider?.name?.toLowerCase() ?? '')),
    [providerNames],
  );

  const close = () => {
    onOpenChange(false);
    setTarget(null);
  };

  const submit = async () => {
    if (!domain?.id || !target?.id) return;
    try {
      const res = await reassign.mutateAsync({ domainId: String(domain.id), serviceId: target.id });
      show({ tone: 'success', title: `Moved ${domain.domain} → ${target.service_name}` });
      onDone();
      close();
      // Follow the domain: land on the destination service's domain list (the
      // scoped list is family-aware, so a per-provider child target resolves to
      // the same console as its umbrella). The reassign hook already
      // invalidated the domain-list caches, so the list arrives fresh.
      const dest = res?.to_service_id ?? target.id;
      navigate(`/services/cdn/${dest}/domains`);
    } catch (e) {
      show({ tone: 'danger', title: 'Move failed', description: errorMessage(e, 'Could not move domain') });
    }
  };

  const providerHint = Array.from(providerNames).join(', ');

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move “{domain?.domain}” to another service</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              Re-points the binding to another service of the same provider
              {providerHint ? <> (<strong>{providerHint}</strong>)</> : null} and organization.
              Analytics history follows the domain — nothing is lost.
            </p>
            {/* Move targets are per-provider CDN services, not orchestrator umbrellas. */}
            <ServiceSelect
              selected={target}
              onSelect={setTarget}
              topLevel={false}
              filter={sameProviderCdn}
              placeholder="Select a target service"
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={close}>Cancel</Button>
          <Button disabled={!target || reassign.isPending} onClick={submit}>
            {reassign.isPending ? 'Moving…' : 'Move domain'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
